import { PlusIcon, XIcon } from '@primer/octicons-react';
import { FieldArray } from 'formik';
import React from 'react';
import { graphql, useFragment } from 'react-relay';

import {
  PromotionPageUpsertDialog_promotion$key,
} from '../../../relay/__generated__/PromotionPageUpsertDialog_promotion.graphql';
import {
  DetailImageDeeplinkInput,
  PromotionPageUpsertDialog_promotionPageUpsertMutation,
} from '../../../relay/__generated__/PromotionPageUpsertDialog_promotionPageUpsertMutation.graphql';
import Button from '../../core/Button';
import ColorPickField from '../../core/ColorPickField';
import Dialog, { DialogProps } from '../../core/Dialog';
import FormLayout from '../../core/FormLayout';
import Grid from '../../core/Grid';
import IconButton from '../../core/IconButton';
import ItemList from '../../core/ItemList';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import Text from '../../core/Text';
import TextField from '../../core/TextField';
import UploadField from '../../core/UploadField';
import View from '../../core/View';
import PromotionPageAppBannerPreview from '../PromotionPageAppBannerPreview';
import PromotionPageAppMiniBannerPreview from '../PromotionPageAppMiniBannerPreview';

const PromotionPageUpsertDialog_promotion = graphql`
  fragment PromotionPageUpsertDialog_promotion on Promotion {
    id
    page {
      bannerBackgroundColor
      bannerImage {
        key
        objectUrl
        size
      }
      bannerSubText
      bannerSubTextColor
      bannerTitle
      bannerTitleColor
      detailBackgroundColor
      detailButtonLink
      detailButtonText
      detailImages {
        image {
          key
          objectUrl
          size
        }
        deeplink
      }
      miniBannerSubText
      miniBannerTitle
    }
  }
`;

type Props = {
  promotion: PromotionPageUpsertDialog_promotion$key;
} & DialogProps &
  Pick<
    MutationFormikProps<PromotionPageUpsertDialog_promotionPageUpsertMutation>,
    'connections' | 'config' | 'onSubmit'
  >;

const PromotionPageUpsertDialog = ({ promotion, connections, config, onSubmit, ...props }: Props) => {
  const { id, page } = useFragment(PromotionPageUpsertDialog_promotion, promotion);

  const defaultImage = { deeplink: '', image: null };

  const dialogActionText = page ? '수정하기' : '입력하기';

  return (
    <Dialog full {...props}>
      <Dialog.Header>
        <Text sx={{ fontWeight: 'bold', fontSize: 3 }}>프로모션 페이지</Text>
      </Dialog.Header>
      <MutationFormik<PromotionPageUpsertDialog_promotionPageUpsertMutation>
        mutation={graphql`
          mutation PromotionPageUpsertDialog_promotionPageUpsertMutation($input: PromotionPageUpsertInput!) {
            promotionPageUpsert(input: $input) {
              bannerTitle
            }
          }
        `}
        initialValues={{
          bannerBackgroundColor: page?.bannerBackgroundColor,
          bannerImage: page?.bannerImage ?? undefined,
          bannerSubText: page?.bannerSubText,
          bannerSubTextColor: page?.bannerSubTextColor,
          bannerTitle: page?.bannerTitle,
          bannerTitleColor: page?.bannerTitleColor,
          detailBackgroundColor: page?.detailBackgroundColor,
          detailButtonLink: page?.detailButtonLink,
          detailButtonText: page?.detailButtonText,
          detailImages: (page?.detailImages ?? [defaultImage]) as DetailImageDeeplinkInput[],
          miniBannerSubText: page?.miniBannerSubText,
          miniBannerTitle: page?.miniBannerTitle,
          promotionId: id,
        }}
        connections={connections}
        config={config}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, handleSubmit }, { nonFieldErrorRef, scrollContainerRef }) => (
          <>
            <Dialog.Body ref={scrollContainerRef} sx={{ position: 'relative' }}>
              <NonFieldError ref={nonFieldErrorRef} sx={{ marginBottom: 3 }} />
              <Grid gapX={4} wrap={false}>
                <Grid.Unit size={'max'}>
                  <FormLayout>
                    <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>배너</Text>
                    <ColorPickField
                      label={'배경색'}
                      name={'bannerBackgroundColor'}
                      placeholder={'배경색 선택'}
                      required
                    />
                    <Grid gapX={2} gapY={2}>
                      <Grid.Unit size={[1, 1, 1 / 2]}>
                        <TextField
                          label={'서브 텍스트'}
                          name={'bannerSubText'}
                          caption={'1줄로 작성, 띄어쓰기 포함 12자 이내'}
                          placeholder={'서브 텍스트 입력'}
                          maxLength={12}
                          required
                        />
                      </Grid.Unit>
                      <Grid.Unit size={[1, 1, 1 / 2]}>
                        <ColorPickField
                          label={'서브 텍스트 색'}
                          name={'bannerSubTextColor'}
                          placeholder={'서브 텍스트 색 선택'}
                          required
                        />
                      </Grid.Unit>
                    </Grid>
                    <Grid gapX={2} gapY={2}>
                      <Grid.Unit size={[1, 1, 1 / 2]}>
                        <TextField
                          label={'타이틀'}
                          name={'bannerTitle'}
                          caption={'* 2줄로 작성, 띄어쓰기 포함 18자 이내\n* 띄어 쓸 문장 사이에 ‘/’를 넣어 주세요.'}
                          placeholder={'타이틀 입력'}
                          value={(values.bannerTitle ?? '').replaceAll('\n', '/')}
                          onChange={(e) => {
                            setFieldValue('bannerTitle', (e.target.value ?? '').replaceAll('/', '\n'));
                          }}
                          validate={(v) => {
                            const value = v ?? '';

                            const enterCount = (value.match(/\n/g) || []).length;
                            if (enterCount > 1) return '2줄 이내로 작성해주세요';

                            if (value.length > 18 + (enterCount ? 1 : 0)) return '18자 이내로 작성해주세요';
                          }}
                          required
                        />
                      </Grid.Unit>
                      <Grid.Unit size={[1, 1, 1 / 2]}>
                        <ColorPickField
                          label={'타이틀 색'}
                          name={'bannerTitleColor'}
                          placeholder={'타이틀 색 선택'}
                          required
                        />
                      </Grid.Unit>
                    </Grid>
                    <UploadField
                      label={'배너 이미지'}
                      name={'bannerImage'}
                      required
                      placeholder={'이미지를 선택하세요'}
                      caption={'* 사이즈 300*300px\n* 파일은 jpg, png를 권장합니다.'}
                      accept={'image/*'}
                    />
                  </FormLayout>
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <View width={280} padding={3}>
                    <Text fontSize={0} color={'fg.subtle'}>
                      배너
                    </Text>
                    <View py={2}>
                      <PromotionPageAppBannerPreview
                        title={values.bannerTitle}
                        subText={values.bannerSubText}
                        backgroundColor={values.bannerBackgroundColor}
                        titleColor={values.bannerTitleColor}
                        subTextColor={values.bannerSubTextColor}
                        imageUrl={values.bannerImage?.objectUrl}
                      />
                    </View>
                    <View lineHeight={'20px'}>
                      <Text fontSize={0} color={'fg.subtle'} whiteSpace={'pre-wrap'}>
                        {
                          '* 이미지는 배너와 미니 배너에 활용됩니다.\n* 배너와 미니 배너의 배경색(#F3F4F6)과 구분되는 이미지로 제작해 주세요.'
                        }
                      </Text>
                    </View>
                  </View>
                </Grid.Unit>
              </Grid>
              <Grid gapX={4} sx={{ pt: 6 }}>
                <Grid.Unit size={'max'}>
                  <FormLayout>
                    <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>미니 배너</Text>
                    <TextField
                      label={'서브 텍스트'}
                      name={'miniBannerSubText'}
                      caption={'1줄로 작성, 띄어쓰기 포함 12자 이내'}
                      placeholder={'서브 텍스트 입력'}
                      maxLength={12}
                      required
                    />
                    <TextField
                      label={'타이틀'}
                      name={'miniBannerTitle'}
                      caption={'1줄로 작성, 띄어쓰기 포함 12자 이내'}
                      placeholder={'타이틀 입력'}
                      maxLength={12}
                      required
                    />
                  </FormLayout>
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <View width={280} padding={3}>
                    <Text fontSize={0} color={'fg.subtle'}>
                      미니 배너
                    </Text>
                    <View py={'12px'}>
                      <PromotionPageAppMiniBannerPreview
                        title={values.miniBannerTitle}
                        subText={values.miniBannerSubText}
                        imageUrl={values.bannerImage?.objectUrl}
                      />
                    </View>
                    <View lineHeight={'20px'}>
                      <Text fontSize={0} color={'fg.subtle'} whiteSpace={'pre-wrap'}>
                        {
                          '* 미니 배너는 배경과 텍스트 색 변경이 불가합니다.\n* 이미지는 배너 이미지를 동일하게 활용합니다.'
                        }
                      </Text>
                    </View>
                  </View>
                </Grid.Unit>
              </Grid>
              <View sx={{ pt: 6 }}>
                <FormLayout>
                  <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>상세 페이지</Text>
                  <ColorPickField
                    label={'배경색'}
                    name={'detailBackgroundColor'}
                    placeholder={'배경색 선택'}
                    required
                  />
                  <Grid gapX={2}>
                    <Grid.Unit size={1 / 2}>
                      <TextField
                        label={'버튼명'}
                        name={'detailButtonText'}
                        caption={"'~하기'의 형식으로 통일성을 맞춰 주세요"}
                        placeholder={'버튼명 입력'}
                        required
                      />
                    </Grid.Unit>
                    <Grid.Unit size={1 / 2}>
                      <TextField
                        label={'링크'}
                        name={'detailButtonLink'}
                        caption={'버튼 클릭 시 이동할 페이지 링크를 적어 주세요'}
                        placeholder={'링크 입력'}
                        required
                      />
                    </Grid.Unit>
                  </Grid>
                  <FieldArray name={'detailImages'}>
                    {({ remove, insert }) => (
                      <>
                        <ItemList
                          items={values?.detailImages || []}
                          renderItem={(_, i) => (
                            <View>
                              <UploadField
                                label={`상세 이미지 ${i + 1}`}
                                name={`detailImages.${i}.image`}
                                required
                                placeholder={'이미지를 선택하세요'}
                                accept={'image/*'}
                              />
                              <View sx={{ mt: 2 }}>
                                <TextField
                                  label={'링크'}
                                  name={`detailImages.${i}.deeplink`}
                                  placeholder={'이미지 링크 입력 (선택)'}
                                  labelConfig={{ visuallyHidden: true }}
                                  caption={
                                    '* 사이즈 너비 900px 이상 (렌더링 시 420px 이하로 리사이징 됨)\n* 파일은 jpg, png를 권장합니다.\n* 링크를 입력하면 이미지 클릭 시 특정 페이지로 이동합니다.'
                                  }
                                />
                              </View>
                            </View>
                          )}
                          renderItemWrapper={(children, _, i) => (
                            <Grid key={i} gapX={2} wrap={false}>
                              <Grid.Unit size={'max'}>{children}</Grid.Unit>
                              {i !== 0 ? (
                                <Grid.Unit size={'min'}>
                                  <IconButton
                                    icon={XIcon}
                                    onClick={() => remove(i)}
                                    aria-label={`remove #${i} image`}
                                    sx={{ marginTop: 4 }}
                                  />
                                </Grid.Unit>
                              ) : null}
                            </Grid>
                          )}
                        />
                        <View sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                          <Button
                            variant={'outline'}
                            onClick={() => insert((values?.detailImages || []).length, defaultImage)}
                            leadingIcon={PlusIcon}
                          >
                            추가하기
                          </Button>
                        </View>
                      </>
                    )}
                  </FieldArray>
                </FormLayout>
              </View>
            </Dialog.Body>

            <Dialog.Footer>
              <Button size={'large'} variant={'primary'} onClick={() => handleSubmit()}>
                {dialogActionText}
              </Button>
            </Dialog.Footer>
          </>
        )}
      </MutationFormik>
    </Dialog>
  );
};

export default PromotionPageUpsertDialog;
